import { backendClient } from "../../api/backend"
import * as types from "./types"

const setLoading = (loading) => ({ type: types.LI_SET_LOADING, payload: loading })
const setError = (error) => ({ type: types.LI_SET_ERROR, payload: error })

// ── Dashboard ───────────────────────────────────────────────────────────────
export const fetchLIDashboard = () => async (dispatch) => {
  dispatch(setLoading(true))
  try {
    const data = await backendClient.getLIOutreachDashboard()
    dispatch({ type: types.LI_SET_DASHBOARD, payload: data })
  } catch (e) {
    dispatch(setError(e.message))
  } finally {
    dispatch(setLoading(false))
  }
}

export const fetchLIDashboardTimeseries = ({ days = 30, account_id = null } = {}) => async (dispatch) => {
  try {
    const data = await backendClient.getLIOutreachTimeseries({ days, account_id })
    dispatch({ type: types.LI_SET_DASHBOARD_TIMESERIES, payload: { days, account_id, data } })
    return data
  } catch (e) {
    dispatch(setError(e.message))
  }
}

export const fetchLIDashboardAccountsHealth = () => async (dispatch) => {
  try {
    const data = await backendClient.getLIOutreachAccountsHealth()
    dispatch({ type: types.LI_SET_DASHBOARD_ACCOUNTS_HEALTH, payload: data })
    return data
  } catch (e) {
    dispatch(setError(e.message))
  }
}

export const fetchLIDashboardCampaignsSummary = () => async (dispatch) => {
  try {
    const data = await backendClient.getLIOutreachCampaignsSummary()
    dispatch({ type: types.LI_SET_DASHBOARD_CAMPAIGNS_SUMMARY, payload: data })
    return data
  } catch (e) {
    dispatch(setError(e.message))
  }
}

// ── Accounts ────────────────────────────────────────────────────────────────
export const fetchLIAccounts = () => async (dispatch) => {
  dispatch(setLoading(true))
  try {
    const data = await backendClient.getLIAccounts()
    dispatch({ type: types.LI_SET_ACCOUNTS, payload: data })
  } catch (e) {
    dispatch(setError(e.message))
  } finally {
    dispatch(setLoading(false))
  }
}

export const fetchLIAccount = (id) => async (dispatch) => {
  try {
    const data = await backendClient.getLIAccount(id)
    dispatch({ type: types.LI_SET_CURRENT_ACCOUNT, payload: data })
    return data
  } catch (e) {
    dispatch(setError(e.message))
  }
}

export const createLIAccount = (data) => async (dispatch) => {
  const result = await backendClient.createLIAccount(data)
  dispatch(fetchLIAccounts())
  return result
}

export const updateLIAccount = (id, data) => async (dispatch) => {
  const result = await backendClient.updateLIAccount(id, data)
  dispatch(fetchLIAccounts())
  return result
}

export const deleteLIAccount = (id) => async (dispatch) => {
  await backendClient.deleteLIAccount(id)
  dispatch(fetchLIAccounts())
}

export const testLIAccountConnection = (id) => async () => {
  return await backendClient.testLIAccountConnection(id)
}

export const refreshLIAccountCookies = (id) => async () => {
  return await backendClient.refreshLIAccountCookies(id)
}

// ── ICPs ────────────────────────────────────────────────────────────────────
export const fetchLIICPs = () => async (dispatch) => {
  dispatch(setLoading(true))
  try {
    const data = await backendClient.getLIICPs()
    dispatch({ type: types.LI_SET_ICPS, payload: data })
  } catch (e) {
    dispatch(setError(e.message))
  } finally {
    dispatch(setLoading(false))
  }
}

export const createLIICP = (data) => async (dispatch) => {
  const result = await backendClient.createLIICP(data)
  dispatch(fetchLIICPs())
  return result
}

export const updateLIICP = (id, data) => async (dispatch) => {
  const result = await backendClient.updateLIICP(id, data)
  dispatch(fetchLIICPs())
  return result
}

export const deleteLIICP = (id) => async (dispatch) => {
  await backendClient.deleteLIICP(id)
  dispatch(fetchLIICPs())
}

export const generateLIICPFormulas = (id) => async (dispatch) => {
  const result = await backendClient.generateLIICPFormulas(id)
  dispatch(fetchLIICPs())
  return result
}

export const runLIICPDiscovery = (id) => async () => {
  return await backendClient.runLIICPDiscovery(id)
}

// ── Leads ────────────────────────────────────────────────────────────────────
export const fetchLILeads = (params) => async (dispatch) => {
  dispatch(setLoading(true))
  try {
    const data = await backendClient.getLILeads(params)
    dispatch({ type: types.LI_SET_LEADS, payload: data })
  } catch (e) {
    dispatch(setError(e.message))
  } finally {
    dispatch(setLoading(false))
  }
}

export const createLILead = (data) => async (dispatch) => {
  const result = await backendClient.createLILead(data)
  dispatch(fetchLILeads())
  return result
}

export const updateLILead = (id, data) => async (dispatch) => {
  const result = await backendClient.updateLILead(id, data)
  return result
}

export const deleteLILead = (id) => async (dispatch) => {
  await backendClient.deleteLILead(id)
  dispatch(fetchLILeads())
}

export const importLILeads = (data) => async () => {
  return await backendClient.importLILeads(data)
}

export const fetchLIImports = () => async (dispatch) => {
  try {
    const data = await backendClient.getLIImports()
    dispatch({ type: types.LI_SET_IMPORTS, payload: data })
  } catch (e) {
    dispatch(setError(e.message))
  }
}

export const validateLILeads = (leadIds, icpId) => async () => {
  return await backendClient.validateLILeads(leadIds, icpId)
}

// ── Campaigns ───────────────────────────────────────────────────────────────
export const fetchLICampaigns = (params) => async (dispatch) => {
  dispatch(setLoading(true))
  try {
    const data = await backendClient.getLICampaigns(params)
    dispatch({ type: types.LI_SET_CAMPAIGNS, payload: data })
  } catch (e) {
    dispatch(setError(e.message))
  } finally {
    dispatch(setLoading(false))
  }
}

export const fetchLICampaign = (id) => async (dispatch) => {
  try {
    const data = await backendClient.getLICampaign(id)
    dispatch({ type: types.LI_SET_CURRENT_CAMPAIGN, payload: data })
    return data
  } catch (e) {
    dispatch(setError(e.message))
  }
}

export const createLICampaign = (data) => async (dispatch) => {
  const result = await backendClient.createLICampaign(data)
  dispatch(fetchLICampaigns())
  return result
}

export const updateLICampaign = (id, data) => async (dispatch) => {
  await backendClient.updateLICampaign(id, data)
  dispatch(fetchLICampaigns())
}

export const deleteLICampaign = (id) => async (dispatch) => {
  await backendClient.deleteLICampaign(id)
  dispatch(fetchLICampaigns())
}

export const activateLICampaign = (id) => async (dispatch) => {
  const result = await backendClient.activateLICampaign(id)
  dispatch(fetchLICampaigns())
  return result
}

export const pauseLICampaign = (id) => async (dispatch) => {
  await backendClient.pauseLICampaign(id)
  dispatch(fetchLICampaigns())
}

export const addLeadsToLICampaign = (campaignId, leadIds) => async () => {
  return await backendClient.addLeadsToLICampaign(campaignId, { lead_ids: leadIds })
}

export const fetchLICampaignLeads = (campaignId, params) => async (dispatch) => {
  try {
    const data = await backendClient.getLICampaignLeads(campaignId, params)
    dispatch({ type: types.LI_SET_CAMPAIGN_LEADS, payload: data })
  } catch (e) {
    dispatch(setError(e.message))
  }
}

export const fetchLICampaignStats = (campaignId) => async (dispatch) => {
  try {
    const data = await backendClient.getLICampaignStats(campaignId)
    dispatch({ type: types.LI_SET_CAMPAIGN_STATS, payload: data })
  } catch (e) {
    dispatch(setError(e.message))
  }
}

// ── Sequence Steps ──────────────────────────────────────────────────────────
export const fetchLISteps = (campaignId) => async (dispatch) => {
  try {
    const data = await backendClient.getLISteps(campaignId)
    dispatch({ type: types.LI_SET_STEPS, payload: data })
  } catch (e) {
    dispatch(setError(e.message))
  }
}

export const createLIStep = (campaignId, data) => async (dispatch) => {
  const result = await backendClient.createLIStep(campaignId, data)
  dispatch(fetchLISteps(campaignId))
  return result
}

export const updateLIStep = (campaignId, stepId, data) => async (dispatch) => {
  await backendClient.updateLIStep(campaignId, stepId, data)
  dispatch(fetchLISteps(campaignId))
}

export const deleteLIStep = (campaignId, stepId) => async (dispatch) => {
  await backendClient.deleteLIStep(campaignId, stepId)
  dispatch(fetchLISteps(campaignId))
}

// ── Inbox ────────────────────────────────────────────────────────────────────
export const fetchLIConversations = (params) => async (dispatch) => {
  dispatch(setLoading(true))
  try {
    const data = await backendClient.getLIConversations(params)
    dispatch({ type: types.LI_SET_CONVERSATIONS, payload: data })
  } catch (e) {
    dispatch(setError(e.message))
  } finally {
    dispatch(setLoading(false))
  }
}

export const fetchLIConversation = (id) => async (dispatch) => {
  try {
    const data = await backendClient.getLIConversation(id)
    dispatch({ type: types.LI_SET_CURRENT_CONVERSATION, payload: data.conversation })
    dispatch({ type: types.LI_SET_MESSAGES, payload: data.messages })
    return data
  } catch (e) {
    dispatch(setError(e.message))
  }
}

export const sendLIReply = (conversationId, body) => async (dispatch) => {
  const result = await backendClient.sendLIReply(conversationId, { body })
  dispatch(fetchLIConversation(conversationId))
  return result
}

export const updateLIConversation = (id, data) => async (dispatch) => {
  await backendClient.updateLIConversation(id, data)
  dispatch(fetchLIConversations())
}

// ── Queue & Log ─────────────────────────────────────────────────────────────
export const fetchLIQueue = (params) => async (dispatch) => {
  try {
    const data = await backendClient.getLIQueue(params)
    dispatch({ type: types.LI_SET_QUEUE, payload: data })
  } catch (e) {
    dispatch(setError(e.message))
  }
}

export const fetchLIActionLog = (params) => async (dispatch) => {
  try {
    const data = await backendClient.getLIActionLog(params)
    dispatch({ type: types.LI_SET_ACTION_LOG, payload: data })
  } catch (e) {
    dispatch(setError(e.message))
  }
}

export const cancelLIAction = (actionId) => async (dispatch) => {
  await backendClient.cancelLIAction(actionId)
  dispatch(fetchLIQueue())
}
